import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import translate from '../../../../commonComponents/translate';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import organizationProp from '../../../../commonComponents/propTypes/organizationProp';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import {
  downloadFile,
  PROFILE_BLOCK,
} from '../../../../services/utilityFunctions';
import Loader from '../../../../services/loader';
import DocumentCard from '../../../../commonComponents/cards/DocumentCard';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import DocumentDropZone from './DocumentDropZone';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ErrorMessage from '../../../../commonComponents/forms/ErrorMessage';
import {
  checkIfHasErrors,
  getErrors,
  getValidationRules,
  isFieldRequiredLabel,
} from '../../helpers/helper';

function TabDocuments({
  t,
  organization,
  member,
  onMemberChange,
  isInJourney,
  onSkipStep,
}) {
  const documentTypes = organization?.documentTypes || [];
  const [documents, setDocuments] = useState(member?.documents || []);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    documents: false,
  });
  const loader = useRef(new Loader('#document'));
  const validationRules = useMemo(
    () =>
      getValidationRules(requiredFieldsPerBlock, PROFILE_BLOCK.DOCUMENTS, {}),
    [getValidationRules, requiredFieldsPerBlock],
  );
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const handleDocuments = useCallback(
    (value, documentTypeId) => {
      setDocuments(prevState => {
        if (prevState.find(doc => doc.documentTypeId === documentTypeId)) {
          return prevState;
        }
        return prevState
          .concat({
            file: value[0],
            documentTypeId,
            date: moment().utc().valueOf(),
          })
          .filter(v => v?.file);
      });
    },
    [setDocuments],
  );

  const showUploadProgress = () => {
    setIsLoading(true);
  };

  const handleUploadLoading = () => {
    setIsLoading(false);
  };

  const handleRemove = useCallback(
    () => file => {
      setDocuments(prevState =>
        prevState.filter(f => f?.file?.url !== file?.url),
      );
    },
    [setDocuments],
  );

  const documentsWithoutDocType = useMemo(
    () =>
      documents.filter(
        d =>
          !documentTypes
            .map(dT => dT?._id?.toString())
            .includes(d?.documentTypeId?.toString()),
      ),
    [documents],
  );

  const handleRemoveFile = useCallback(
    file => {
      setDocuments(prevState =>
        prevState.filter(f => f?.file?.url !== file?.url),
      );
    },
    [setDocuments],
  );

  const handleSubmit = async () => {
    const newErrors = getErrors(validationRules, { documents });

    if (checkIfHasErrors(newErrors)) {
      setErrors(newErrors);
      return;
    }
    onMemberChange({
      documents,
    });
  };

  return (
    <div className="m-portlet__body profileTabDocuments">
      <div className="documents-header">
        <div className="documents-header__title">
          <FormSubtitle
            title={isFieldRequiredLabel(
              requiredFieldsPerBlock,
              PROFILE_BLOCK.DOCUMENTS,
              'documents',
              t('profile_tab_documents'),
            )}
            titleClass={`mainTitle ${errors?.documents ? 'label-error' : ''}`}
          />
          {errors?.documents && (
            <ErrorMessage styleMessage={{ marginLeft: '-16px' }} />
          )}
        </div>
        {isInJourney && onSkipStep && <SkipStepButton onClick={onSkipStep} />}
      </div>
      <div className="tabDocuments">
        {documentTypes?.length > 0 ? (
          documentTypes.map(d => (
            <DocumentDropZone
              key={d?.file?.url}
              documents={documents}
              docType={d}
              handleDocuments={handleDocuments}
              showUploadProgress={showUploadProgress}
              handleUploadLoading={handleUploadLoading}
              handleRemove={!isInJourney ? handleRemove : null}
            />
          ))
        ) : (
          <p className="no-data-hint">{t('report.documentsTypes.notFound')}</p>
        )}
      </div>
      {!isInJourney && (
        <>
          <FormSubtitle
            title={t('other.documents')}
            titleClass="mainTitle secondTitle"
          />
          <div className="tabOtherDocs">
            {documentsWithoutDocType?.length > 0 ? (
              documentsWithoutDocType.map(d => (
                <DocumentCard
                  file={d?.file}
                  onClickDownload={() => downloadFile(d?.file)}
                  onRemoveFile={() => handleRemoveFile(d?.file)}
                  id={d?.file?.url}
                  removeColor="#FF36A6"
                />
              ))
            ) : (
              <p className="no-data-hint">{t('report.documents.notFound')}</p>
            )}
          </div>
        </>
      )}
      <FormFooter submitHandler={handleSubmit} isLoading={isLoading} />
    </div>
  );
}

TabDocuments.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  organization: PropTypes.shape(organizationProp.propType).isRequired,
  member: PropTypes.shape(UserProp.propType).isRequired,
  onMemberChange: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func,
  isInJourney: PropTypes.bool,
};

TabDocuments.defaultProps = {
  isInJourney: false,
  onSkipStep: null,
};

export default translate(TabDocuments);
